// Listen: Breeze on a call held somewhere else. Adam, 2026-10-03: "if i say
// would you mind i record the meeting, can we have breeze live but listening
// into our convo on teams, on my sidebar like she'd be on my platform?"
//
// No video call. His microphone is his side; the tab or system audio he shares
// from Teams (or Zoom, or Meet) is the other person's. Each goes up as its own
// seat (hooks/useListen.ts), so the Room tells the speakers apart by stream and
// the Breeze panel fills in beside the call the way it does in a Breeze call.

import { useEffect, useId, useMemo, useRef, useState, type JSX } from "react";
import { parseListenQuery } from "@breeze/core";
import type { AccountUser, ListenSaved } from "@breeze/core";
import { BreezeStrip } from "../components/BreezeStrip";
import { ListenInline, SAVED_HOLD_MS, SavedMark, SavedRow } from "../components/ListenInline";
import { CallPrepWorkspace } from "../components/CallPrepWorkspace";
import { WorkspaceSheet } from "../components/WorkspaceSheet";
import { BreezeMarkIcon, MicIcon, PrepIcon, ScreenShareIcon } from "../components/icons";
import type { AssistantStore } from "../hooks/useAssistant";
import { useDesktopControls } from "../hooks/useDesktopControls";
import { listenWord, savedWord, useListen, type SideState } from "../hooks/useListen";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { navigate } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";

interface ListenScreenProps {
  readonly me: AccountUser;
}

/** A 0 to 1 reading of how loud a track is right now, so each side can show
 * that sound is arriving. Reads the track; sends nothing anywhere. */
function useLevel(track: MediaStreamTrack | null): number {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!track) {
      setLevel(0);
      return;
    }
    const Ctx: typeof AudioContext | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    let ctx: AudioContext;
    try {
      ctx = new Ctx();
    } catch {
      return;
    }
    void ctx.resume().catch(() => undefined);
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const data = new Uint8Array(analyser.fftSize);
    const timer = setInterval(() => {
      analyser.getByteTimeDomainData(data);
      let peak = 0;
      for (const v of data) peak = Math.max(peak, Math.abs(v - 128));
      setLevel(Math.min(1, (peak / 128) * 2.2));
    }, 120);
    return () => {
      clearInterval(timer);
      try {
        source.disconnect();
      } catch {
        // Already disconnected.
      }
      void ctx.close().catch(() => undefined);
    };
  }, [track]);
  return level;
}

function Meter({ track }: { readonly track: MediaStreamTrack | null }): JSX.Element {
  const level = useLevel(track);
  return (
    <div className="listen__meter" aria-hidden="true">
      <div className="listen__meter-fill" style={{ transform: `scaleX(${level})` }} />
    </div>
  );
}

// Adam, 2026-10-05: "i want to disable the say next prompts, soft disable with
// a toggle, for all important calls. leave ones with clay and trusted people.
// it's more of a distraction right now than it is anything useful and i don't
// want it to become a crutch." Then: "trusted i mean people that are not sales
// prospects." The page does not know who is a prospect, so the link says: the
// agendas add say=on to a call with anyone else. The order is what he set on
// the page for that guest, then the link, then Clay, then off.
const SAY_KEY = "breeze.listen.say.";

function sayKey(guest: string): string {
  return SAY_KEY + guest.trim().toLowerCase();
}

export function sayNextDefault(guest: string, hint: "on" | "off" | "" = ""): boolean {
  const name = guest.trim().toLowerCase();
  try {
    const kept = localStorage.getItem(sayKey(name));
    if (kept === "on") return true;
    if (kept === "off") return false;
  } catch {
    // Storage is blocked: fall through to the name.
  }
  if (hint) return hint === "on";
  return /\bclay\b/.test(name);
}

function keepSayNext(guest: string, on: boolean): void {
  const name = guest.trim().toLowerCase();
  if (!name) return;
  try {
    localStorage.setItem(sayKey(name), on ? "on" : "off");
  } catch {
    // Not kept; the default applies next time.
  }
}

/** The same store with the line to say next taken out, for every surface. */
function withoutSay(store: AssistantStore): AssistantStore {
  if (!store.host || store.host.say === null) return store;
  return { ...store, host: { ...store.host, say: null } };
}

function statusWord(side: SideState, idle: string): string {
  switch (side.status) {
    case "on":
      return "Recording";
    case "asking":
      return "Waiting for your choice";
    case "lost":
      return "Stopped";
    case "off":
      return "Not available";
    default:
      return idle;
  }
}

// Adam, 2026-10-05: "when i pull it to half screen like this it just gives me
// the notes there, while continuing to listen, and i want a minimal listening
// indicator with a stop button there as well. when expanded horizontally it
// can still show all that stuff currently on the left side, but since i'm in
// light mode it should also be in light mode." A window under about 1100px on
// a computer is the half-screen case.
const NOTES_ONLY_QUERY = "(max-width: 1100px) and (hover: hover) and (pointer: fine)";

export function ListenScreen({ me }: ListenScreenProps): JSX.Element {
  const target = useRef(parseListenQuery(window.location.search)).current;
  const store = useListen(target, me);
  const dock = useDesktopControls();
  const notesOnly = useMediaQuery(NOTES_ONLY_QUERY);
  const prepSlug = target.prepSlug || null;
  const [prepOpen, setPrepOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [sayOn, setSayOn] = useState(() => sayNextDefault(target.guest, target.say));
  const sayId = useId();
  const assistant = useMemo(() => (sayOn ? store.assistant : withoutSay(store.assistant)), [sayOn, store.assistant]);
  const appWord = target.app || "call";
  const shareLabel = store.theirs.status === "on" ? "Share again" : target.app ? `Share ${target.app} audio` : "Share call audio";

  const [leaving, setLeaving] = useState(false);
  const leave = (): void => {
    if (store.phase === "failed") {
      store.end();
      navigate(ownRoomPath());
      return;
    }
    setLeaving(true);
    store.end();
  };
  // After End or Stop: the recording is shown saving, then saved, and only
  // then does the page move on.
  const outcome = store.saved && store.saved !== "saving" ? store.saved : null;
  const closing = leaving || store.saved !== null;
  useEffect(() => {
    if (!closing || !outcome || store.phase !== "ended") return;
    const timer = setTimeout(() => navigate(ownRoomPath()), SAVED_HOLD_MS);
    return () => clearTimeout(timer);
  }, [closing, outcome, store.phase]);

  if (closing && store.phase !== "failed") {
    const saved = store.saved ?? "saving";
    return (
      <div className={notesOnly ? "listen listen--notes" : "listen listen--center"} data-testid="listen-saved" data-saved={saved}>
        {notesOnly ? (
          <SavedRow saved={saved} testId="listen-saved-row" />
        ) : (
          <div className="listen__saved">
            <SavedMark saved={saved} size={56} />
            <p className="listen__saved-word" role="status">
              {savedWord(saved)}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (store.phase === "failed") {
    return (
      <div className="listen listen--center">
        <p className="listen__problem" role="alert">
          {store.error ?? "Could not start."}
        </p>
        <button type="button" className="listen__btn listen__btn--primary" onClick={leave}>
          Back
        </button>
      </div>
    );
  }

  const ready = store.phase === "live" || store.phase === "reconnecting";

  if (notesOnly && ready) {
    const heard = store.theirs.status === "on";
    return (
      <div className="listen listen--notes" data-testid="listen-notes-only">
        <div className="lnotes__bar">
          <span className="lsi__dot" aria-hidden="true" data-quiet={heard && !store.paused ? "0" : "1"} />
          <span className="lnotes__word" role="status">
            {store.phase === "reconnecting" ? "Reconnecting" : listenWord(store)}
          </span>
          <button type="button" className="lnotes__btn" onClick={() => store.setPaused(!store.paused)} aria-pressed={store.paused}>
            {store.paused ? "Resume" : "Pause"}
          </button>
          {heard ? null : (
            <button type="button" className="lnotes__btn" onClick={() => void store.shareOtherSide()} disabled={store.theirs.status === "asking"}>
              {shareLabel}
            </button>
          )}
          <button type="button" className="lnotes__stop" onClick={leave}>
            Stop
          </button>
        </div>
        <div className="lnotes__body">
          <div className="cpw">
            <CallPrepWorkspace slug={prepSlug} panel={store.panel} assistant={assistant} selfId={store.selfId} isHost sharingScreen={false} />
          </div>
        </div>
      </div>
    );
  }
  const panelNode =
    ready && panelOpen ? (
      dock ? (
        <div className="cpw">
          <CallPrepWorkspace slug={prepSlug} panel={store.panel} assistant={assistant} selfId={store.selfId} isHost sharingScreen={false} columns />
        </div>
      ) : (
        <BreezeStrip panel={store.panel} assistant={assistant} selfId={store.selfId} isHost sharingScreen={false} onClose={() => setPanelOpen(false)} />
      )
    ) : null;

  return (
    <div className="listen">
      <div className="listen__main">
        <header className="listen__head">
          <div className="listen__title">
            <BreezeMarkIcon size={22} />
            <h1>Listen</h1>
          </div>
          <div className="listen__head-actions">
            {ready && dock ? (
              <button type="button" className="listen__btn" aria-pressed={panelOpen} onClick={() => setPanelOpen((v) => !v)}>
                <PrepIcon size={18} />
                Agenda
              </button>
            ) : null}
            {ready && !dock ? (
              <button type="button" className="listen__btn" onClick={() => setPrepOpen(true)}>
                <PrepIcon size={18} />
                Agenda
              </button>
            ) : null}
            {ready && !dock && !panelOpen ? (
              <button type="button" className="listen__btn" onClick={() => setPanelOpen(true)}>
                <BreezeMarkIcon size={18} />
                Breeze
              </button>
            ) : null}
            {ready ? (
              <button type="button" className="listen__btn" aria-pressed={store.paused} onClick={() => store.setPaused(!store.paused)}>
                {store.paused ? "Resume" : "Pause"}
              </button>
            ) : null}
            <button type="button" className="listen__btn listen__btn--end" onClick={leave}>
              End
            </button>
          </div>
        </header>

        <p className="listen__lead">
          {store.phase === "starting"
            ? "Starting"
            : store.phase === "reconnecting"
              ? "Reconnecting"
              : store.paused
                ? "Paused. Nothing is heard or recorded until you resume."
                : `Breeze is listening to your ${appWord}${target.guest ? ` with ${target.guest}` : ""}.`}
        </p>

        <section className="listen__sides" aria-label="Sound sources">
          <article className="listen__side" data-status={store.mine.status}>
            <div className="listen__side-top">
              <MicIcon size={20} />
              <div className="listen__side-name">
                <strong>{store.hostName}</strong>
                <span>Your microphone</span>
              </div>
              <span className="listen__side-status">{statusWord(store.mine, "Starting")}</span>
            </div>
            <Meter track={store.mine.track} />
            {store.mine.message ? <p className="listen__side-note">{store.mine.message}</p> : null}
          </article>

          <article className="listen__side" data-status={store.theirs.status}>
            <div className="listen__side-top">
              <ScreenShareIcon size={20} />
              <div className="listen__side-name">
                <strong>{store.guestName}</strong>
                <span>{target.app ? `${target.app} audio` : "Call audio"}</span>
              </div>
              <span className="listen__side-status">{statusWord(store.theirs, "Not shared")}</span>
            </div>
            <Meter track={store.theirs.track} />
            {store.theirs.message ? (
              <p className="listen__side-note" role={store.theirs.status === "off" ? "alert" : undefined}>
                {store.theirs.message}
              </p>
            ) : null}
            <button
              type="button"
              className={store.theirs.status === "on" ? "listen__btn" : "listen__btn listen__btn--primary"}
              onClick={() => void store.shareOtherSide()}
              disabled={!ready || store.theirs.status === "asking"}
            >
              {store.theirs.status === "off" && store.theirs.message ? "Try again" : shareLabel}
            </button>
            {store.theirs.status === "idle" || store.theirs.status === "lost" ? (
              <p className="listen__side-hint">
                In the picker, choose the {target.app ? `${target.app} tab` : "tab"} and turn on Share tab audio, or choose the whole screen and turn on Share system audio.
              </p>
            ) : null}
          </article>
        </section>

        <div className="listen__switch">
          <label htmlFor={sayId}>Say-next prompts</label>
          <input
            id={sayId}
            type="checkbox"
            role="switch"
            className="bp__toggle"
            checked={sayOn}
            onChange={(e) => {
              setSayOn(e.target.checked);
              keepSayNext(target.guest, e.target.checked);
            }}
            data-testid="listen-say-switch"
          />
        </div>

        <ul className="listen__notes">
          <li>Tell the other person Breeze is listening and recording before you start.</li>
          <li>Wear headphones, so their voice does not reach your microphone.</li>
          <li>The sound and the video of what you share are recorded here, not by the other app. End stops both sources and files the call.</li>
        </ul>
      </div>
      {panelNode}
      {prepOpen && !dock ? (
        <WorkspaceSheet title="Agenda" tall onClose={() => setPrepOpen(false)}>
          <CallPrepWorkspace slug={prepSlug} panel={store.panel} assistant={assistant} selfId={store.selfId} isHost sharingScreen={false} />
        </WorkspaceSheet>
      ) : null}
    </div>
  );
}

/** Listen inside a frame (the Calls card in Seamless): one row, no screen
 * around it, and nothing navigates when it stops. The page around it hears
 * what happens through postMessage (components/ListenInline.tsx). */
export function ListenEmbed({ me }: ListenScreenProps): JSX.Element {
  const target = useRef(parseListenQuery(window.location.search)).current;
  const [stopped, setStopped] = useState<ListenSaved | "closed" | null>(null);
  useEffect(() => {
    document.documentElement.setAttribute("data-embed", "1");
    return () => document.documentElement.removeAttribute("data-embed");
  }, []);
  if (stopped) {
    // The confirmation stays: the page around the frame decides when it closes.
    return (
      <div className="linl--embed" data-testid="listen-embed-stopped">
        <SavedRow saved={stopped === "closed" ? "none" : stopped} testId="listen-embed-saved" />
      </div>
    );
  }
  return (
    <div className="linl--embed">
      <ListenInline target={target} me={me} embed onStop={(saved) => setStopped(saved ?? "closed")} />
    </div>
  );
}
